/**
 * The four marks on the bar that moves between the shop's books.
 *
 * <p>A mark and the word, never a mark on its own. These four are not icons
 * anybody has seen before - a leaf does not obviously mean "the grower side" to
 * someone who has never used this - so the Urdu word is what names the section
 * and the shape is only what the eye lands on first. Dropping the words would
 * trade a second of reading for a week of guessing.
 *
 * <p>Drawn inline rather than pulled from an icon library. Four shapes do not
 * justify a dependency, and these are drawn to one grid - 24 units, 1.8 stroke,
 * round caps - so they sit at the same weight beside each other instead of one
 * looking heavier than the rest.
 */

type MarkProps = { size?: number }

const common = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

/** Customers: the people who owe the shop. */
export function CustomersMark({ size = 22 }: MarkProps) {
  return (
    <svg className="sections__mark" width={size} height={size} {...common}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.6a3.2 3.2 0 0 1 0 6" />
      <path d="M17.5 14.6a5.5 5.5 0 0 1 3 4.9" />
    </svg>
  )
}

/** Growers: what arrives from the fields. */
export function VendorsMark({ size = 22 }: MarkProps) {
  return (
    <svg className="sections__mark" width={size} height={size} {...common}>
      <path d="M12 21v-7" />
      <path d="M12 14c0-3.3 2.4-6 5.5-6.4C17.2 11 15 14 12 14Z" />
      <path d="M12 14c0-3.3-2.4-6-5.5-6.4C6.8 11 9 14 12 14Z" />
      <path d="M8.5 21h7" />
    </svg>
  )
}

/** Reports: what the shop earned. */
export function ReportsMark({ size = 22 }: MarkProps) {
  return (
    <svg className="sections__mark" width={size} height={size} {...common}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="m7.5 15 3.5-4 3 2.5L20 7" />
      <path d="M16.5 7H20v3.5" />
    </svg>
  )
}

/** Everything else: the bin, the backup, the names, the settings. */
export function MoreMark({ size = 22 }: MarkProps) {
  return (
    <svg className="sections__mark" width={size} height={size} {...common}>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  )
}
